import React from 'react';
import Slider from 'react-slick';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import ProductDetailsTab from './ProductDetailsTab';
import properties from '../../../modules/ajio-util/properties';
import Modal from '../../common/Modal';
import { showModal, hideModal } from '../../../actions/modal.js';
import { generateEvent } from '../../../utils/dataGrinch';
import { getTagValueFromTagEngine, getWishlistCountFromTagEngine } from '../../../utils/product';
import { isExclusiveDatePast } from '../../../utils/products';
import luxeScreen from '../../../refresh/constants/luxeScreenType';
import {
  getProductItemPDP,
  getMultiSellerImageDetailsProps,
  gtmEventPush,
  segmentEventPush
} from '../../../modules/ga/GTMdatalayer';
import { signInCheck } from '../../common/CheckUserStatus';
import {
  getEnableComingSoonProducts,
  getEnableNonReturnableProductRevamp,
  getMultiSellerSlaFlag
} from '../../../refresh/selectors/featureFlagsSelector';
import {
  getIsComingSoonProduct,
  getIsNonReturnable,
  getProductFnlReturnWindow,
  getEddDetails,
  getSpotlightData
} from '../../../refresh/selectors/productpage';
import { replaceWithJioCDN } from '../../../../src/utils/imageHandling';
import { getValidAttributionToken } from '../../../utils/common'

// Tracks the productCode for which the "Pdp Product Image Details" event has already fired.
// This View is wrapped in an AB `Feature` (enable_urgency_driver_pdp) that mounts variant
// "0", then switches variant and remounts a fresh View — which would fire this mount-time
// event twice. This module-level guard lives outside the remounting component so the event
// is emitted only once per product view (resets on full page reload).
let imageDetailsEventFiredForProduct = null;

class View extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      zoomImgStyle: 0,
      cursor: 'default',
      isLoaded: false
    };
    this.myCarouselItemsRef = React.createRef();
    this.tabRefs = [];
    // Tracks whether the next slide change was triggered by a real user gesture
    this.userInteractedWithCarousel = false;
  }

  markCarouselInteraction = () => {
    this.userInteractedWithCarousel = true;
  };

  handleError(event) {
    event.target.src = `${properties.getProperty('imageBaseUrl')}/static/img/plp.png`;
  }

  handleTabFocus = () => {
    if (this.props.updateIsPlay)
      this.props.updateIsPlay(false)
  };

  handleTabBlur = () => {
    if (this.props.updateIsPlay)
      this.props.updateIsPlay(true)
  };

  componentDidMount() {
    if (this.myCarouselItemsRef.current) {
      this.myCarouselItemsRef.current.addEventListener('focusin', this.handleTabFocus.bind(this));
      this.myCarouselItemsRef.current.addEventListener('focusout', this.handleTabBlur.bind(this));
      this.myCarouselItemsRef.current.addEventListener('pointerdown', this.markCarouselInteraction);
      this.myCarouselItemsRef.current.addEventListener('keydown', this.markCarouselInteraction);
      const filterByImgType = image => {
        if (image.format === 'product' && image?.imageType !== 'PRIMARY') {
          return true;
        }
      };
      let filteredImgList = this.props.imgdata?.images?.filter(filterByImgType);

      // Guard against the event firing twice when the AB `Feature` wrapper remounts this
      // View (see imageDetailsEventFiredForProduct declaration).
      const currentProductCode = this.props.imgdata?.code;
      if (imageDetailsEventFiredForProduct === currentProductCode) return;
      imageDetailsEventFiredForProduct = currentProductCode;

      const validToken = getValidAttributionToken();
      const {
        emiDetails = {},
        returnWindow,
        enableNonReturnableProductRevamp,
        isNonReturnable,
        imgdata = {},
        eddDetailsForProductDetails = {},
        productCatalog = '',
        aggregateRating = {},
        sizeData = '',
        similarData = [],
        spotlightData = [],
        isNewProductTag = false
      } = this.props;
      const {
        fnlColorVariantData: { productTag: rawProductTag, exclusiveTill = '' } = {},
        tagResponse: { tag: { categoryTags = [] } = {} } = {}
      } = imgdata;
      const productTagEngineValue = getTagValueFromTagEngine(categoryTags);
      const productTagValue = isExclusiveDatePast(exclusiveTill) ? 'EXCLUSIVE' : rawProductTag;
      const wishlistedCountValue = getWishlistCountFromTagEngine(categoryTags);

      const deliveryTypes = [];
      if (eddDetailsForProductDetails?.servicability) deliveryTypes.push('Standard');
      if (eddDetailsForProductDetails?.priorityDelivery?.deliverySLA) {
        deliveryTypes.push('Priority|NDD');
      }
      if (eddDetailsForProductDetails?.rushDelivery?.deliverySLA) deliveryTypes.push('Rush|1HR');
      const deliveryType = deliveryTypes.join(',');

      const videoPosition = imgdata?.images?.findIndex(img => img.isVideo) + 1 || 'NA';
      const sizeChartAvailable = sizeData?.sizechart?.length > 0;
      // SizeGuide is the 7th sub-component in Description.js render order.
      const DESKTOP_SIZE_CHART_FIXED_POSITION = 7;
      const spotlightPositions =
        spotlightData
          ?.map(sp => sp?.attributeValue)
          ?.filter(Boolean)
          ?.join(', ') || '';

      segmentEventPush({
        event: 'Pdp Product Image Details',
        tenure_months: emiDetails?.featuredPlan?.tenure,
        emi_amount: emiDetails?.featuredPlan?.totalMonthlyAmountPayable,
        interest_rate_percent:
          emiDetails?.featuredPlan?.type === 'NO_COST'
            ? 0
            : emiDetails?.featuredPlan?.bankInterestRate,
        emi_min_order_value: emiDetails?.featuredPlan?.thresholdAmount,
        emi_offer_type: [
          emiDetails?.featuredPlan?.instantDiscountApplied && 'Instant Discount',
          emiDetails?.featuredPlan?.cashbackApplied && 'Cashback'
        ]
          .filter(Boolean)
          .join(', '),
        emi_applicable: !!emiDetails?.featuredPlan,
        screen_source: 'pdp',
        video_position: videoPosition,
        similar_icon_flag: similarData.length > 0,
        size_chart_icon_flag: sizeChartAvailable,
        size_chart_position: sizeChartAvailable ? DESKTOP_SIZE_CHART_FIXED_POSITION : 'NA',
        products: [
          {
            ...getProductItemPDP(imgdata),
            ...{ number_of_images: filteredImgList?.length },
            no_of_color_variants: imgdata?.fnlColorVariantData?.colorVariantCount || 0,
            product_spotilght_position: spotlightPositions,
            product_servicability: eddDetailsForProductDetails?.servicability ?? false,
            standard_delivery_edd:
              eddDetailsForProductDetails?.deliverySLA ||
              eddDetailsForProductDetails?.eddUpper ||
              '',
            priority_delivery_edd: eddDetailsForProductDetails?.priorityDelivery?.deliverySLA || '',
            rush_delivery_edd: eddDetailsForProductDetails?.rushDelivery?.deliverySLA || '',
            delivery_type: deliveryType,
            product_tag: isNewProductTag ? productTagEngineValue : productTagValue,
            product_rating: aggregateRating?.averageRating ?? '',
            number_of_ratings_collected:
              imgdata?.ratingCount ?? aggregateRating?.numUserRatings ?? '',
            catalogue_id: productCatalog,
            product_wishlisted_count: wishlistedCountValue,
            product_return_window: returnWindow ?? 0,
            product_exchange_window: returnWindow ?? 0,
            ...(enableNonReturnableProductRevamp && {
              atb_non_returnable_visible: isNonReturnable ? 'yes' : 'no'
            }),
            ...getMultiSellerImageDetailsProps(this.props.imgdata, this.props.enableMultiSellerSla)
          }
        ],
        no_of_color_variants: imgdata?.fnlColorVariantData?.colorVariantCount || 0,
        number_of_images: filteredImgList?.length || 0,
        attribution_token: validToken
      });
    }
  }
  componentWillUnmount() {
    if (this.myCarouselItemsRef.current) {
      this.myCarouselItemsRef.current.removeEventListener('focusin', this.handleTabFocus);
      this.myCarouselItemsRef.current.removeEventListener('focusout', this.handleTabBlur);
      this.myCarouselItemsRef.current.removeEventListener(
        'pointerdown',
        this.markCarouselInteraction
      );
      this.myCarouselItemsRef.current.removeEventListener('keydown', this.markCarouselInteraction);
    }
  }

  onActionClick = e => {
    let ensamble = document.getElementById('ensamble');
    let ensambleOffsetTop = ensamble.offsetTop - 150;
    let element = document.body;
    let duration = 500;
    if (duration <= 0) return;
    var difference = ensambleOffsetTop - element.scrollTop;
    var perTick = (difference / duration) * 10;

    setTimeout(function () {
      element.scrollTop += perTick;
      if (element.scrollTop === ensambleOffsetTop) return;
      scrollTo(element, ensambleOffsetTop, duration - 10);
    }, 10);
  };

  componentDidUpdate(prevProps) {
    try {
      if (prevProps.isPlay !== this.props.isPlay) {
        if (this.props.isPlay)
          this.myCarouselItemsRef.current.setAttribute('aria-live', 'off');
        else
          this.myCarouselItemsRef.current.setAttribute('aria-live', 'polite');
      }
    } catch (error) {
      console.log(error)
    }
  }

  // componentWillReceiveProps(nextProps) {
  //   let nextId = nextProps.selectedImageId;
  //   if (nextProps.isTabPanelArrowControl && !nextProps.isPlay) {
  //     this.slider.slickGoTo(nextId);
  //     // this.props.onSelect(nextId)
  //     clearTimeout(this.timer);
  //     this.slider.innerSlider.pause();
  //     return
  //   }
  //   if (nextProps.isPlay) {
  //     this.slider.slickGoTo(nextId);
  //     clearTimeout(this.timer);
  //     this.timer = setTimeout(() => {
  //       this.slider.innerSlider.play();
  //     }, 5200);
  //   } else {
  //     clearTimeout(this.timer);
  //     this.slider.innerSlider.pause();
  //   }
  // }

  componentDidUpdate(prevProps) {
    const prevId = prevProps.selectedImageId;
    const nextId = this.props.selectedImageId;
 
    if (this.props.isTabPanelArrowControl && !this.props.isPlay && prevId !== nextId) {
      if (this.slider) {
        this.slider.slickGoTo(nextId);
        clearTimeout(this.timer);
        this.slider.innerSlider.pause();
      }
      return;
    }
 
    if (prevProps.isPlay !== this.props.isPlay || prevId !== nextId) {
      if (this.slider) {
        this.slider.slickGoTo(nextId);
        clearTimeout(this.timer);
 
        if (this.props.isPlay) {
          this.timer = setTimeout(() => {
            this.slider.innerSlider.play();
          }, 5200);
        } else {
          this.slider.innerSlider.pause();
        }
      }
    }
  }

  luxeEventPush = ({ eventName, color, ...rest }) => {
    const { pdp: { screenType = '', screenName = '' } = {} } = luxeScreen;
    const { selectedSizeCode, selectedSizeName, discountPercentage, imgdata } = this.props;

    const {
      brickSubCategory,
      brickName,
      name: productName,
      code: productCode,
      fnlColorVariantData: { color: productColor, brandName, productTag }
    } = imgdata;
    let eventPushObj = {
      product_details: {
        product_name: productName,
        product_id: selectedSizeCode || productCode,
        product_brand: brandName,
        product_price: '11',
        product_color: color || productColor,
        product_size: selectedSizeName,
        product_trade_discount: discountPercentage,
        product_catogory: brickSubCategory,
        product_brick: brickName,
        product_tag: productTag
      },
      user_details: {
        store_type: 'luxe',
        user_status: signInCheck() ? 'logged in' : 'not logged In',
        time_of_the_day: '',
        login_type: signInCheck() ? 'logged in' : 'not logged In',
        screen_name: screenName,
        screen_type: screenType
      }
    };
    let pushDataObj =
      rest.isViewProductDetails && rest.isViewProductDetails === 'no'
        ? { ...eventPushObj.user_details }
        : { ...eventPushObj.user_details, ...eventPushObj.product_details };
    delete rest.isViewProductDetails;
    gtmEventPush({
      event: eventName,
      event_category: 'product details interactions',
      ...pushDataObj,
      ...rest
    });
  };
  render() {
    const { imgdata, isComingSoonEnabled, isPlay = true, isTabPanelArrowControl = false, hasFocus = false } = this.props;
    const { isComingSoonProduct } = imgdata;
    const newDesignLuxe = this.props.newDesignLuxe;
    let selectedImageId = this.props.selectedImageId;
    let onSelect = this.props.onSelect;
    const { isLoaded = false } = this.state;
    const imgPath = properties.getProperty("imageBaseUrl");

    const { reasonForNotServiceability } = this.props.eddDetailsForProductDetails;
    const settings = {
      infinite: true,
      slidesToShow: 1,
      slidesToScroll: 1,
      autoplay: !isTabPanelArrowControl,
      autoplaySpeed: 5000,
      fade: true,
      lazyLoad: true,
      lazyLoadOffset: 2,
      afterChange(currentSlide) {
        // generateEvent({
        //   "Event": "ProductSwipe",
        //   value : {
        //     ImageIndex: currentSlide
        //   }
        // })
        onSelect(currentSlide);        
        
      },
      beforeChange: (oldIndex, newIndex) => {
        // Only fire on a real user gesture (swipe/arrow/keyboard) and on an
        // actual index change — never on the autoplay timer, which would
        // otherwise emit this event every autoplaySpeed (5s) indefinitely.
        if (oldIndex !== newIndex && this.userInteractedWithCarousel) {
          segmentEventPush({
            event: 'Pdp Product Image Details Interaction',
            products: [
              {
                images_scrolled: newIndex,
                ...getProductItemPDP(imgdata)
              }
            ]
          });
        }
        this.userInteractedWithCarousel = false;
      }
    };

    const secureLogo = '/static/img/pdp-shield.png';

    const zoomSettings = {
      infinite: true,
      dots: true,
      initialSlide: selectedImageId,
      slidesToShow: 1,
      slidesToScroll: 1,
      autoplay: false,
      pauseOnHover: true,
      fade: true,
      lazyLoad: true,
      lazyLoadOffset: 2
    };
    const prodData = this.props.imgdata;
    let SizeWornByModel = '';
    let ModelChestSize = '';
    let ModelHeight = '';
    let ModelWaistSize = '';

    let stockLevelStatus = 'outOfStock';
    var sizeVariant;
    prodData?.baseOptions?.map((variant, i) => {
      if (variant?.variantType === 'FnlSizeVariant') {
        sizeVariant = variant?.options;
      }
    });
    if (sizeVariant === undefined) {
      sizeVariant = prodData?.variantOptions;
    }
    const filterByStock = size => {
      if (size?.stock && size?.stock?.stockLevelStatus !== 'outOfStock') {
        stockLevelStatus = size?.stock?.stockLevelStatus;
      }
    };
    sizeVariant?.filter(filterByStock);

    const filterByImgType = image => {
      if (image.format === 'product' && image?.imageType !== 'PRIMARY') {
        return true;
      }
    };
    const filterZoomByImgType = image => {
      if (image.format === 'superZoomPdp' && image.imageType !== 'PRIMARY') {
        return true;
      }
    };
    const {
      images = [],
      code = '',
      baseProduct: baseProductCode = '',
      fnlColorVariantData = {},
      tagResponse: { tag: { categoryTags = [] } = {} } = {},
      name = '',
      brandName: brandAltName = '',
      url,
      images: primaryImg = []
    } = prodData;
    const { exclusiveTill = '', productTag = '' } = fnlColorVariantData;
    const productTagValue = isExclusiveDatePast(exclusiveTill)
      ? 'EXCLUSIVE'
      : productTag?.toLowerCase() !== 'exclusive'
      ? productTag
      : '';
    const productTagEngineValue = getTagValueFromTagEngine(categoryTags);
    let filteredImgList = images.filter(filterByImgType);
    let filteredZoomImgList = images.filter(filterZoomByImgType);
    const { location } = this.props;
    const { pathname } = location;
    let zoomImageList = filteredZoomImgList.map((image, i) => {
      return (
        <div key={i}>
          <div className="img-container" style={{backgroundImage: `url(${`${imgPath}/static/img/plp.png`})`}}>
            <img
              id={`silder-image${i}`}
              className={`rilrtl-lazy-img img-alignment ${isLoaded ? 'rilrtl-lazy-img-loaded' : ''
                }`}
              onLoad={() => {
                this.setState({ isLoaded: true });
              }}
              style={{
                position: 'relative',
                cursor: this.state.cursor,
                top: this.state.zoomImgStyle
              }}
              ref={scaledImage => {
                this.scaledImage = scaledImage;
              }}
              src={`${replaceWithJioCDN(image.url)}`}
              alt={`Product image of ${brandAltName} ${name}`}
              onMouseMove={event => {
                const element = document.getElementById(`silder-image${i}`);
                let topLimit = element?.clientHeight - window?.innerHeight;
                let setZoomPosition = event.clientY < topLimit;
                if (setZoomPosition) {
                  let position = parseInt(`-${event.clientY}`);
                  this.setState({ zoomImgStyle: position, cursor: 'pointer' });
                }
              }}
            />
          </div>
        </div>
      );
    });
    let { shopTheLook = {}, isNewProductTag } = this.props;
    let { products = [], ensembleLongTitle = '' } = shopTheLook;
    let imageList = filteredImgList.map((image, i) => {
      return (
        <div key={i} class="carousel-item"
          id={`carousel-item-${i + 1}`}
          role="tabpanel"
          aria-roledescription="slide"
          aria-label={`${i + 1} of ${filteredImgList.length}`}
          aria-hidden={selectedImageId != i}
          ref={(el) => (this.tabRefs[i] = el)}
        >
          {this.props.viewRender === 'product-page' ? (
            <div className="img-container" style={{backgroundImage: `url(${`${imgPath}/static/img/plp.png`})`}}>
              {isComingSoonEnabled && productTag && productTag.trim() !== '' && (
                <div style={{ position: 'absolute', top: '10px', left: '10px', backgroundColor: 'rgba(0, 0, 0, 1)', color: 'white', padding: '5px', fontSize: '12px', zIndex: 10 }}>
                  {productTag.toUpperCase()}
                </div>
              )}
              <img
                className={`rilrtl-lazy-img img-alignment zoom-cursor ${isLoaded ? 'rilrtl-lazy-img-loaded' : ''
                  }`}
                onLoad={() => {
                  this.setState({ isLoaded: true });
                }}
                src={`${replaceWithJioCDN(image.url)}`}
                alt={`Product image of ${brandAltName} ${name} ${(isComingSoonEnabled && productTag && productTag.trim() !== '') ? productTag.toUpperCase() : ''}`}
                onError={this.handleError}
                onClick={() => {
                  generateEvent({
                    Event: 'ProductZoom',
                    value: {
                      ImageIndex: selectedImageId,
                      ProductId: baseProductCode
                    }
                  });
                  this.props.showModal('zoomed-img');
                }}
                // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                tabIndex={selectedImageId == i ? 0 : -1}
              />
              {products.length > 0 && ensembleLongTitle && (
                <div className="ensamble-btn" onClick={this.onActionClick}>
                  {ensembleLongTitle}{' '}
                </div>
              )}
              {!isComingSoonEnabled && (
                <>
                  {stockLevelStatus === 'outOfStock' ? (
                    <>
                      {newDesignLuxe ? (
                        <div className="product-oos">This product is currently Out of Stock.</div>
                      ) : null}
                    </>
                  ) : isNewProductTag && productTagEngineValue ? (
                    <div className={newDesignLuxe ? 'luxe-pdp-exclusive-new' : 'pdp-exclusive-new'}>
                      {productTagEngineValue}
                    </div>
                  ) : productTagValue ? (
                    <div className="pdp-exclusive" aria-label={productTagValue}>
                      {productTagValue}
                    </div>
                  ) : (
                    ''
                  )}</>)}
            </div>
          ) : (
            <div className="img-container">
              {/* eslint-disable-next-line jsx-a11y/img-redundant-alt, jsx-a11y/img-redundant-alt */}
              <img
                className="img-alignment"
                src={`${replaceWithJioCDN(image.url)}`}
                alt={`Product image of ${brandAltName} ${name}`}
                onError={this.handleError}
              />
              {isNewProductTag && productTagEngineValue ? (
                <div className={newDesignLuxe ? 'luxe-pdp-exclusive-new' : 'pdp-exclusive-new'}>
                  {productTagEngineValue}
                </div>
              ) : productTagValue ? (
                <div className="pdp-exclusive" aria-label={productTagValue}>
                  {productTagValue}
                </div>
              ) : (
                ''
              )}
            </div>
          )}
        </div>
      );
    });
    let { brandName = '', color = '' } = fnlColorVariantData;
    let pageTitle = `Buy ${color} ${brandName} ${name} | AJIO`;
    let websiteURL = `${properties.getProperty('siteUrl')}${url}`;
    let imageBaseUrl = `${properties.getProperty('imageBaseUrl')}`;
    const pdpSocialPrimaryImageUrl = primaryImg[0]?.url;
    return (
      <Row>
        <Col xs={1} aria-hidden="true" tabIndex={-1} />
        <Col xs={10} className="img-mv-left">
          {this.props.viewRender === 'product-page' && (
            <div className="social-share-icons">
              <div
                className="sprite-img prod-share-block"
                role="button"
                tabIndex={0}
                aria-expanded={this.state.isShareOpen}
                aria-label="Share product on social media"
                onClick={() => this.setState({ isShareOpen: !this.state.isShareOpen })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    this.setState({ isShareOpen: !this.state.isShareOpen });
                  } else if (e.key === 'Escape' && this.state.isShareOpen) {
                    this.setState({ isShareOpen: false });
                  }
                }}
              />
              <div
                className="prod-share-tooltip"
                role='list'
                aria-hidden={!this.state.isShareOpen}
                style={{ display: this.state.isShareOpen ? 'block' : 'none' }}
              >
                <div className="sprite-img lft-arw" aria-hidden={true} />
                <a
                  href={`https://www.facebook.com/sharer.php?u=${websiteURL}&amp;t=${pageTitle}`}
                  className="sprite-img facebook-blk"
                  style={{backgroundImage: `url(${`${imageBaseUrl}/static/img/sprite_v1.png`})`, backgroundRepeat: "no-repeat"}} 
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Share on Facebook"
                  tabIndex={this.state.isShareOpen ? 0 : -1}
                  onClick={() => {
                    this.luxeEventPush({
                      eventName: 'product_share',
                      event_action: 'product share',
                      event_label: 'Facebook'
                    });
                    let {
                      baseProduct: baseProductCode = '',
                      name = '',
                      verticalColor: color = '',
                      code = ''
                    } = prodData;
                    generateEvent({
                      Event: 'Share',
                      value: {
                        Medium: 'Facebook',
                        ProductId: baseProductCode
                      }
                    });

                    segmentEventPush({
                      event: 'Share Clicked',
                      screen_name: 'PDP page',
                      products:[{
                        ...getProductItemPDP(prodData)
                      }]
                   

                    })
                  }}
                />
                <a
                  href={`https://twitter.com/intent/tweet?url=${websiteURL}`}
                  className="sprite-img twitter-blk"
                  style={{backgroundImage: `url(${`${imageBaseUrl}/static/img/sprite_v1.png`})`, backgroundRepeat: "no-repeat"}} 
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Share on Twitter"
                  tabIndex={this.state.isShareOpen ? 0 : -1}
                  onClick={() => {
                    this.luxeEventPush({
                      eventName: 'product_share',
                      event_action: 'product share',
                      event_label: 'Twitter'
                    });
                    let {
                      baseProduct: baseProductCode = '',
                      name = '',
                      verticalColor: color = '',
                      code = ''
                    } = prodData;
                    generateEvent({
                      Event: 'Share',
                      value: {
                        Medium: 'Twitter',
                        ProductId: baseProductCode
                      }
                    });
                    segmentEventPush({
                      event: 'Share Clicked',
                      screen_name: 'PDP page',
                      products:[{
                        ...getProductItemPDP(prodData)
                      }]

                    })
                  }}
                />
                <a
                  href={`http://pinterest.com/pin/create/button/?url=${websiteURL}&amp;description=${pageTitle}&amp;media=${websiteURL}${pdpSocialPrimaryImageUrl}`}
                  className="sprite-img pinterest-blk"
                  style={{backgroundImage: `url(${`${imageBaseUrl}/static/img/sprite_v1.png`})`, backgroundRepeat: "no-repeat"}} 
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Share on Pinterest"
                  tabIndex={this.state.isShareOpen ? 0 : -1}
                  onClick={() => {
                    this.luxeEventPush({
                      eventName: 'product_share',
                      event_action: 'product share',
                      event_label: 'Pininterest'
                    });
                    let {
                      baseProduct: baseProductCode = '',
                      name = '',
                      verticalColor: color = '',
                      code = ''
                    } = prodData;
                    generateEvent({
                      Event: 'Share',
                      value: {
                        Medium: 'Pininterest',
                        ProductId: baseProductCode
                      }
                    });
                    segmentEventPush({
                      event: 'Share Clicked',
                      screen_name: 'PDP page',
                      products:[{
                        ...getProductItemPDP(prodData)
                      }]

                    })
                  }}
                />
                <a
                  href={`https://plus.google.com/share?url=${websiteURL}&amp;utm_content=${pageTitle}&amp;utm_campaign=&amp;utm_medium=share%2Bbutton`}
                  className="sprite-img google-plus-blk"
                  style={{backgroundImage: `url(${`${imageBaseUrl}/static/img/sprite_v1.png`})`, backgroundRepeat: "no-repeat"}} 
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Share on Google+"
                  tabIndex={this.state.isShareOpen ? 0 : -1}
                  onClick={() => {
                    this.luxeEventPush({
                      eventName: 'product_share',
                      event_action: 'product share',
                      event_label: 'Google Plus'
                    });
                    let {
                      baseProduct: baseProductCode = '',
                      name = '',
                      verticalColor: color = '',
                      code = ''
                    } = prodData;
                    generateEvent({
                      Event: 'Share',
                      value: {
                        Medium: 'Google Plus',
                        ProductId: baseProductCode
                      }
                    });
                    segmentEventPush({
                      event: 'Share Clicked',
                      screen_name: 'PDP page',
                      products:[{
                        ...getProductItemPDP(prodData)
                      }]

                    })
                  }}
                />
              </div>
            </div>
          )}
          <div className="image-slick-container" id="main-content">
            <section id="myCarousel"
              class="carousel-tablist"
              aria-roledescription="carousel"
              aria-label={name}>
              <div id="myCarousel-items"
                data-modify-arrow-color
                class="carousel-items playing"
                aria-live="off"
                ref={this.myCarouselItemsRef}
              >
                <Slider ref={c => (this.slider = c)} {...settings}>
                  {imageList}
                </Slider>
              </div>
            </section>
            {((isComingSoonEnabled && !isComingSoonProduct) || !isComingSoonEnabled) && stockLevelStatus === 'outOfStock' && (
              <div className="oos-message">This product is currently out of stock.</div>
            )}

          </div>
          {prodData.featureData &&
            prodData.featureData.map((list, i) =>
              list.featureValues.map((sublist, j) => {
                if (list.name === 'Size worn by Model' && sublist.value) {
                  SizeWornByModel = sublist.value;
                }
                if (list.name === 'Model Chest Size' && sublist.value) {
                  ModelChestSize = sublist.value;
                }
                if (list.name === 'Model Height' && sublist.value) {
                  ModelHeight = sublist.value;
                }
                if (list.name === 'Model Waist Size' && sublist.value) {
                  ModelWaistSize = sublist.value;
                }
              })
            )}
          {this.props.viewRender === 'product-page' && !newDesignLuxe && (
            <div className="pdp-model-desc">
              {SizeWornByModel && (
                <div aria-label={`${SizeWornByModel ? 'Our model wears a Size' : ''}`}>
                  {SizeWornByModel && <span>Our model wears a Size {SizeWornByModel}</span>}
                  {(ModelHeight || ModelChestSize || ModelWaistSize) && <span>,&nbsp;with</span>}
                  {ModelHeight && <span>&nbsp;Height {ModelHeight} </span>}
                  {ModelChestSize ? (
                    ModelChestSize && ModelWaistSize ? (
                      <span>,&nbsp;Chest {ModelChestSize}</span>
                    ) : (
                      <span>and&nbsp;Chest {ModelChestSize}</span>
                    )
                  ) : (
                    ''
                  )}
                  {ModelWaistSize ? <span>and&nbsp;Waist {ModelWaistSize}</span> : ''}
                </div>
              )}
            </div>
          )}
          {/* {newDesignLuxe ? 
          <div className="payment-products">
            <div className="genuine-secure">
              <img src={secureLogo} alt="Trustmarker icon" />
              <div>Secure Payments</div>
            </div>
            <div className="genuine-secure">
              <img src={secureLogo} alt="Trustmarker icon" />
              <div>Genuine Products</div>
            </div>
            <div className="genuine-secure">
              <img src={secureLogo} alt="Trustmarker icon" />
              <div>Genuine Products</div>
            </div>
          </div>
          : 
          null
          } */}
          {this.props.viewRender === 'product-page' && (
            <ProductDetailsTab prodData={prodData} newDesignLuxe={newDesignLuxe} />
          )}
        </Col>
        <Col xs={1} aria-hidden="true" tabIndex={-1} />
        <Modal name="zoomed-img" className="zoomed-img">
          <div className="zoom-img-slick">
            <Slider
              ref={c => {
                this.zoomSlider = c;
                if (this.zoomSlider) {
                  this.zoomSlider.innerSlider.list.setAttribute('tabindex', 0);
                  this.zoomSlider.innerSlider.list.focus();
                }
              }}
              {...zoomSettings}
              accessibility
            >
              {zoomImageList}
            </Slider>
          </div>
        </Modal>
      </Row>
    );
  }
}

function mapStateToProps(state, ownProps) {
  return {
    shopTheLook: state.product.ensamble,
    // eddDetails is keyed [pincode][sizeCode] — getEddDetails drills both levels
    eddDetailsForProductDetails: getEddDetails(state),
    selectedSizeCode: state.product.selectedSizeCode,
    selectedSizeName: state.product.selectedSizeName,
    discountPercentage: state.product.discountPercentage,
    isComingSoonEnabled: getEnableComingSoonProducts(state?.flags) && getIsComingSoonProduct(state),
    enableNonReturnableProductRevamp: getEnableNonReturnableProductRevamp(state),
    isNonReturnable: getIsNonReturnable(state),
    returnWindow: getProductFnlReturnWindow(state),
    productCatalog: state.product?.productDetails?.catalog || '',
    aggregateRating: state.product?.productDetails?.ratingsResponse?.aggregateRating || {},
    sizeData: state.product?.sizeData || '',
    similarData: state.product?.similarData || [],
    spotlightData: getSpotlightData(state),
    enableMultiSellerSla: getMultiSellerSlaFlag(state)
  };
}

function mapDispatchToProps(dispatch, ownProps) {
  return {
    showModal: (name, config) => {
      dispatch(showModal(name, config));
    },
    hideModal: name => {
      dispatch(hideModal(name));
    }
  };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(View));
